<template>
  <v-container fluid>
    <avs-backButton
      @click.stop="$store.commit('app/setProject', project)"
      backTo="Project"
    />

    <AvsSteppers
      :value="tab"
      @change="v => (tab = v)"
    >
      <template v-slot:headers>
        <AvsStep
          step="1"
          :rules="[() => isFormValid.description]"
        >
          {{ $t("commons.informations") }}
          <small v-show="!isFormValid.description">
            {{ $t("commons.validation_issues") }}
          </small>
        </AvsStep>
        <v-divider></v-divider>
        <AvsStep step="2">{{ $t("views.team.title") }}</AvsStep>
      </template>

      <template v-slot:items>
        <v-stepper-content step="1">
          <v-form
            ref="form-description"
            v-model="isFormValid.description"
            class="mt-5"
          >
            <v-text-field
              v-model="project.name"
              :rules="rules.name"
              counter="255"
              maxlength="255"
              clearable
              dense
              outlined
              :label="$t('commons.name')"
              type="text"
            />

            <v-textarea
              v-model="project.description"
              :rules="rules.description"
              clearable
              dense
              outlined
              :label="$t('commons.description')"
              rows="4"
            />
            <!-- === Nouveau champ color === -->
            <v-text-field
              v-model="project.color"
              type="color"
              dense
              outlined
              :label="$t('Couleur de l\'espace')"
              class="mt-3"
            />

            <ImageInput
              v-model="project.logo"
              dense
              :spanName="'Logo'"
            />
            <ImageInput
              v-model="project.picture"
              dense
              :spanName="'Image de couverture'"
            />
          </v-form>
        </v-stepper-content>

        <v-stepper-content step="2">
          <ProjectTeam v-if="isProjectCreated" />
        </v-stepper-content>
      </template>

      <template v-slot:actions>
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-left"
          @click="tab--"
          :disabled="tab <= 1"
          :name="$t('commons.previous')"
        />
        <avs-stepButton
          class="mx-2"
          icon="mdi-chevron-right"
          right
          @click="tabIncrement"
          :name="nextLabel"
        />
      </template>
    </AvsSteppers>
  </v-container>
</template>

<script>
import _ from "lodash"
import { ProjectService } from "@/services"
import { ImageInput } from "@/components"
import ProjectTeam from "@/views/project/ProjectTeam.vue"
// import { NEXT } from "@/utils/consts"
import { mkProject } from "@/utils/datamodels"
import { required } from "@/utils/validator"
import { toISODate } from "@/utils/datetime"

export default {
  name: "ProjectCreateUpdate",
  components: {
    AvsStep: () => import("@/components/generics/general/AvsStep.vue"),
    AvsSteppers: () => import("@/components/generics/general/AvsSteppers.vue"),
    ImageInput,
    ProjectTeam
  },
  data: function () {
    const company = this.$store.getters["app/getCompany"]

    return {
      projectRef: null,
      project: mkProject(company, "Nom de l'espace"),
      tab: this.$route.params.tab || 1,
      loading: false,
      rules: {
        name: [required],
        description: [],
        department: []
      },
      isFormValid: {
        description: false,
        steps: true
      },

      nextLabel: this.$t("commons.next"), //NEXT.DEFAULT,
      isProjectCreated: false,
      showInput: false
    }
  },
  methods: {
    toISODate,
    notify(msg, color = "success") {
      this.$store.commit("app/message", { msg, color })
    },
    prepareProjectForSubmission(project) {
      return {
        ...project,
        department: project.department ? project.department.id : null
      }
    },
    async saveProject() {
      this.project.lang = this.$i18n.locale
      this.project.department = this.project.department ? this.project.department.id : null
      const isFormDescriptionValid = this.$refs["form-description"].validate()
      if (isFormDescriptionValid) {
        const saveMethod = this.isProjectCreated ? this.updateProject : this.createProject
        // const preparedProject = this.prepareProjectForSubmission(this.project)
        return saveMethod()
          .then(savedId => {
            this.getProject(savedId)
          })
          .catch(error => {
            // API saving error redirect to tab 1
            this.tab = 1
          })
      } else {
        // Invalid form error redirect to tab 2
        this.notify(this.$t("messages.warning.all_required_info"), "warning")
        this.tab = 1

        const msg = `IsFormDescriptionValid: ${isFormDescriptionValid}`
        return Promise.reject(msg)
      }
    },
    async getProject(projectId) {
      return ProjectService.get(this, null, projectId).then(r => {
        this.project = {
          ...r
          // Order steps by start_date ascend
          // steps: r.steps.sort((a, b) => a.start_date > b.start_date)
        }
        this.projectRef = _.cloneDeep(this.project)
        this.storeProject()
        this.isProjectCreated = true
      })
    },
    tabIncrement() {
      return this.tab < 2 ? this.tab++ : this.$router.push({ name: "Project" })
    },
    async createProject() {
      this.loading = true
      return ProjectService.create(this, this.project)
        .then(response => {
          this.notify(this.$t("messages.success.created", { entity: this.$tc("Espace", 1) }))
          return response.id
        })
        .finally(() => {
          this.loading = false
        })
    },
    async updateProject() {
      return new Promise((resolve, reject) => {
        let requests = []
        const projectAttrs = ["name", "description", "company", "logo", "picture", "color"]
        const pickProject = _.pick(this.project, projectAttrs)
        const pickProjectRef = _.pick(this.projectRef, projectAttrs)
        if (!_.isEqual(pickProjectRef, pickProject)) {
          requests.push(ProjectService.update(this, this.project.id, pickProject))
        }

        if (requests.length > 0) {
          this.loading = true
          Promise.all(requests)
            .then(() => {
              this.notify(
                this.$t("messages.success.updated", {
                  entity: this.$tc("Espace", 1)
                })
              )
            })
            .finally(() => {
              this.loading = false
              resolve(this.project.id)
            })
        } else {
          this.notify(this.$t("messages.success.no_updated"), "info")
          resolve(this.project.id)
        }
      })
    },
    storeProject() {
      this.$store.commit("app/setProject", this.project)
    }
  },
  created() {
    if (this.projectId) this.getProject(this.projectId)
  },
  watch: {
    tab(newValue, oldValue) {
      // Set Save label
      if (newValue === 1) {
        this.nextLabel = this.$t("commons.save_next") //NEXT.SAVE
      } else if (newValue === 2) {
        this.nextLabel = this.$t("commons.finish") //NEXT.FINISH
      }

      if (oldValue === 1 && newValue === 2) {
        this.saveProject()
      }
    }
  },
  computed: {
    projectId() {
      return this.$route.params.id || this.project.id || null
    },
    title() {
      return this.projectId
        ? this.$t("actions.edit", { entity: this.$tc("categories.project.name", 1).toLowerCase() })
        : this.$t("actions.initiate", {
            entity: this.$tc("categories.project.name", 1).toLowerCase()
          })
    }
  },
  mounted() {
    if (this.$route.query?.chosenTab) {
      this.tab = this.$route.query.chosenTab
    }
  }
}
</script>

<style scoped>
.activity-title {
  border-left: 5px solid #452078;
}
.item-container {
  border: 1px solid rgba(0, 0, 0, 0.25);
  border-radius: 4px;
  margin-bottom: 16px;
}
.task-title {
  border-left: 5px solid #a4c11f;
}
</style>
